import { publicBluesky } from './public-bluesky';
import type { ActorProfile } from '../domain/types';
import {
  getRecentSkyspacePeople,
  searchSkyspaceDirectory,
} from './site-services';
import {
  mergeProfileAesthetics,
  normalizeClassicTheme,
  parseLegacyAesthetics,
  type ClassicTheme,
} from '../domain/classic-profile';

export const queryKeys = {
  profile: (handle: string) => ['profile', handle] as const,
  authorFeed: (handle: string) => ['author-feed', handle] as const,
  skyspaceContent: (did: string) => ['skyspace-content', did] as const,
  profileAppearance: (did: string) => ['profile-appearance', did] as const,
  search: (
    query: string,
    onlyInterests = false,
    searchType: 'skyspace' | 'users' = 'skyspace',
  ) => ['search', searchType, query, onlyInterests] as const,
  topFriends: (
    handle: string,
    top8: string,
    visibility = 'public',
  ) => ['top-friends', handle, top8, visibility] as const,
  comments: (handle: string) => ['comments', handle] as const,
  replies: (handle: string, uris: string[]) => ['replies', handle, ...uris] as const,
  supporters: ['supporters'] as const,
  latestPeople: ['latest-people'] as const,
  siteModeration: ['site-moderation'] as const,
};

export async function loadSkyspaceProfileTheme(
  person: Pick<ActorProfile, 'did' | 'handle'>,
  signal?: AbortSignal,
): Promise<ClassicTheme | null> {
  try {
    const [content, authorFeed] = await Promise.all([
      publicBluesky.getSkyspaceContent(person.did, signal).catch(() => undefined),
      publicBluesky.getAuthorFeed(person.handle, 100, signal).catch(() => undefined),
    ]);
    const legacyAesthetics = parseLegacyAesthetics(authorFeed?.feed || []);
    const hasSkyspaceProfile = Boolean(
      content?.aesthetics
      || content?.content
      || legacyAesthetics,
    );
    if (!hasSkyspaceProfile) return null;
    const aesthetics = mergeProfileAesthetics(
      legacyAesthetics,
      content?.aesthetics?.value,
    );
    return normalizeClassicTheme(aesthetics);
  } catch {
    return null;
  }
}

export async function searchSkyspacePeoplePage(
  query: string,
  onlyInterests = false,
  cursor?: string,
  signal?: AbortSignal,
): Promise<{ people: ActorProfile[]; cursor?: string }> {
  if (!query && !onlyInterests && !cursor) {
    const recent = await getRecentSkyspacePeople(signal);
    const batches = Array.from(
      { length: Math.ceil(recent.length / 25) },
      (_, index) => recent.slice(index * 25, index * 25 + 25),
    );
    const profiles = (await Promise.all(
      batches.map(batch => publicBluesky.getProfiles(
        batch.map(person => person.handle),
        signal,
      )),
    )).flat();
    const order = new Map(recent.map((person, index) => [person.did, index]));
    return {
      people: profiles.sort((left, right) => (
        (order.get(left.did) ?? Number.MAX_SAFE_INTEGER)
        - (order.get(right.did) ?? Number.MAX_SAFE_INTEGER)
      )),
    };
  }
  const page = await searchSkyspaceDirectory(query, onlyInterests, cursor, signal);
  const people = await publicBluesky.getProfiles(
    page.people.map(person => person.handle),
    signal,
  );
  const order = new Map(page.people.map((person, index) => [person.did, index]));
  return {
    people: people.sort((left, right) => (
      (order.get(left.did) ?? Number.MAX_SAFE_INTEGER)
      - (order.get(right.did) ?? Number.MAX_SAFE_INTEGER)
    )),
    cursor: page.cursor,
  };
}

export async function searchSkyspacePeople(
  query: string,
  signal?: AbortSignal,
): Promise<ActorProfile[]> {
  return (await searchSkyspacePeoplePage(query, false, undefined, signal)).people;
}

export async function searchBlueskyUsersPage(
  query: string,
  cursor?: string,
  signal?: AbortSignal,
): Promise<{ people: ActorProfile[]; cursor?: string }> {
  const response = await publicBluesky.searchActors(query, 50, signal, cursor);
  return { people: response.actors || [], cursor: response.cursor };
}
